import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject, of, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { Branch, ApiModel, OrderedItemType, OrderedItem, RequestOptions, S3FileModel, S3File } from 'models';
import { getItemAsJson, setItemAsJson } from 'helpers';
import { UserService } from 'services/user.service';
import { SegmentService } from 'services/segment.service';
import { PreorderArticle, PreorderArticleSize, PrepackForPreorder } from 'app/b2b-order/types/preorder';
import { OrderSheetItem } from './types';
import { HttpHeaders } from '@angular/common/http';
import { TrackProcess } from 'helpers/monitoring';
import { TrackingEvents } from 'services/tracking/gtm';
import { getItemIdentifier } from 'app/b2b-order/helpers/shopping-cart';
import { getTotalPriceByItem } from 'app/b2b-order/helpers/order';
import { CartErrors } from 'app/b2b-order/components/shopping-cart/shopping-cart.component';

const manufacturerOrderSheetStorageKey = 'manufacturerOrderSheetItems1';
const retailerOrderSheetStorageKey = 'orderSheetItems1';

@Injectable({
    providedIn: 'root'
})
export class OrderSheetService {
    public items: BehaviorSubject<OrderSheetItem[]> = new BehaviorSubject([]);
    public count: Observable<number>;
    public isOrderValid: Observable<boolean>;
    public isAllowedToDowloadPDFConfirmation: Observable<boolean>;
    public amountOfNewArticles: Subject<number> = new Subject();
    public errorMessages: BehaviorSubject<OrderErrorMessage[] | null>;
    public isOrderBeingPlaced = new BehaviorSubject(false);

    private listOfItems: OrderSheetList;
    private storageKey: string;

    constructor(
        private apiModel: ApiModel,
        private s3FileModel: S3FileModel,
        private userService: UserService,
        private segmentService: SegmentService
    ) {
        this.storageKey = this.userService.isManufacturer()
            ? manufacturerOrderSheetStorageKey
            : retailerOrderSheetStorageKey;
        this.listOfItems = getItemAsJson(this.storageKey) || {};
        this.items.next(_.values(this.listOfItems));
        this.count = this.items.pipe(map((items) => _.size(_.keys(items))));
        this.isOrderValid = this.items.pipe(map((items) => this.hasValidItemsForOrder(items)));

        this.errorMessages = new BehaviorSubject(null);
        this.isAllowedToDowloadPDFConfirmation = this.s3FileModel.getJSON(S3File.OrganizationFeatureFlags).pipe(
            map((featureList) => {
                try {
                    const userOrganizationId = this.userService.getOrganizationId();
                    return featureList.ORDER_SHEET_PDF_DOWNLOAD.some(
                        (organizationId) => organizationId === userOrganizationId
                    );
                } catch (error) {
                    console.error('Error while retriving ORDER_SHEET_PDF_DOWNLOAD feature flag configuration', {
                        error
                    });
                    return false;
                }
            }),
            catchError(() => of(false))
        );
    }

    @TrackProcess('DownloadPdf')
    public printOrder({
        store,
        retailerOrderNumber,
        retailerId
    }: {
        store?: Branch;
        retailerOrderNumber: string;
        retailerId: string;
    }): Promise<void> {
        this.segmentService.track('PDF download triggered');
        const headers = new HttpHeaders({ Accept: 'application/pdf' });
        const address = store ? { ...store.address, name: store.name } : {};
        const requestOptions: RequestOptions = {
            method: 'post',
            url: `${this.apiModel.getPreorderEndpoint()}/download/pdf/confirmation`,
            headers,
            body: {
                orderedItems: this.getItemsToOrder(),
                ...(!_.isEmpty(address) ? { shippingAddress: address } : {}),
                retailerId,
                ...(!_.isEmpty(retailerOrderNumber) ? { retailerOrderNumber } : {})
            }
        };

        return this.apiModel
            .makeHttpObservableBlobRequest(requestOptions)
            .pipe(
                tap((response) => {
                    const { body: blob, headers } = response;
                    const filename = headers.get('Content-Disposition').split('=')[1].replace(/\"/g, '');
                    const downloadElement = document.createElement('a');
                    const objectUrl = URL.createObjectURL(blob);
                    downloadElement.href = objectUrl;
                    downloadElement.download = Boolean(filename) ? filename : 'order_confirmation.pdf';
                    downloadElement.click();
                    URL.revokeObjectURL(objectUrl);
                })
            )
            .toPromise()
            .then((response) => null);
    }

    public async sendOrder({
        store,
        retailerId,
        retailerContactEmail,
        additionalConfirmationEmail,
        retailerOrderNumber,
        notes
    }: {
        store: Branch;
        retailerId: string;
        retailerContactEmail?: string;
        additionalConfirmationEmail?: string;
        retailerOrderNumber?: string;
        notes?: string;
    }) {
        if (this.isOrderBeingPlaced.getValue()) {
            throw throwError(new Error('OrderBeingPlaced'));
        }
        try {
            this.isOrderBeingPlaced.next(true);
            const url = `${this.apiModel.getPreorderEndpoint()}/order`;
            const address = { ...store.address, name: store.name };
            await this.apiModel.makeHttpRequest({
                method: 'POST',
                url,
                body: {
                    orderedItems: this.getItemsToOrder(),
                    ...(!_.isEmpty(retailerOrderNumber) ? { retailerOrderNumber } : {}),
                    shippingAddress: address,
                    ...(store.billingAddress ? { billingAddress: { ...address, gln: store.billingAddress.gln } } : {}),
                    ...(store.buyerAddress ? { buyerAddress: { ...address, gln: store.buyerAddress.gln } } : {}),
                    ...(store.locationServiceId ? { storeId: store.locationServiceId } : {}),
                    ...(!_.isEmpty(notes) ? { notes } : {}),
                    ...(!_.isEmpty(retailerContactEmail) ? { retailerContactEmail } : {}),
                    retailerId,
                    ...(!_.isEmpty(additionalConfirmationEmail) ? { additionalConfirmationEmail } : {})
                }
            });

            this.errorMessages.next([]);
            this.isOrderBeingPlaced.next(false);
            this.trackPreorder();
        } catch (error) {
            this.errorMessages.next([
                {
                    message:
                        error.data.type === 'OrderingProcessError'
                            ? OrderError.OrderingProcessError
                            : OrderError.Unknown,
                    data: { error }
                }
            ]);
            throw error;
        }
    }

    public async saveDraft({
        store,
        retailerId,
        retailerContactEmail,
        additionalConfirmationEmail,
        retailerOrderNumber,
        notes
    }: {
        store?: Branch;
        retailerId: string;
        retailerContactEmail?: string;
        additionalConfirmationEmail?: string;
        retailerOrderNumber?: string;
        notes?: string;
    }) {
        const url = `${this.apiModel.getPreorderDraftEndpoint()}`;

        const address = store ? { ...store.address, name: store.name } : {};
        const billingAddress = store && store.billingAddress ? { ...address, gln: store.billingAddress.gln } : {};
        const buyerAddress = store && store.buyerAddress ? { ...address, gln: store.buyerAddress.gln } : {};

        await this.apiModel.makeHttpRequest({
            method: 'POST',
            url,
            body: {
                orderedItems: this.getItemsToOrder(),
                retailerId,
                ...(!_.isEmpty(retailerOrderNumber) ? { retailerOrderNumber } : {}),
                ...(!_.isEmpty(address) ? { shippingAddress: address } : {}),
                ...(!_.isEmpty(billingAddress) ? { billingAddress } : {}),
                ...(!_.isEmpty(buyerAddress) ? { buyerAddress } : {}),
                ...(!_.isEmpty(notes) ? { notes } : {}),
                ...(!_.isEmpty(retailerContactEmail) ? { retailerContactEmail } : {}),
                ...(!_.isEmpty(additionalConfirmationEmail) ? { additionalConfirmationEmail } : {})
            }
        });
    }

    public add(items: OrderSheetItem[]): void {
        _.forEach(items, (item) => (this.listOfItems[getItemIdentifier(item)] = item));
        this.amountOfNewArticles.next(_.size(items));
        this.changed();
    }

    public remove(item: OrderSheetItem): void {
        this.listOfItems = _.omit(this.listOfItems, getItemIdentifier(item));
        this.changed();
    }

    public clear(isUserAction = false): void {
        if (isUserAction) {
            this.trackClearOrderSheet();
        }

        this.listOfItems = {};
        this.changed();
    }

    public getItemsObservable() {
        return this.items.asObservable();
    }

    public getValidOrderSheetItemsFromPreorderArticles(articles: PreorderArticle[]) {
        return _.reduce(
            articles,
            (cartItems: OrderSheetItem[], article: PreorderArticle) => {
                const orderedSizes = _.filter(article.sizes, ({ stock }) => stock.selectedAmount > 0);
                const orderedPrepacks = _.filter(article.prepacks, ({ stock }) => stock.selectedAmount > 0);
                const hasOrderedPrepacks = !_.isEmpty(orderedPrepacks);
                const hasOrderedSizes = !_.isEmpty(orderedSizes);

                return [
                    ...cartItems,
                    ...(hasOrderedPrepacks || hasOrderedSizes
                        ? [
                              {
                                  ...article,
                                  prepacks: hasOrderedPrepacks ? orderedPrepacks : [],
                                  sizes: hasOrderedPrepacks ? article.sizes : orderedSizes
                              }
                          ]
                        : [])
                ];
            },
            []
        );
    }

    public getItemFromList(item: OrderSheetItem) {
        return this.listOfItems[getItemIdentifier(item)];
    }

    private trackPreorder() {
        const itemList = _.values(this.listOfItems);

        const itemsWithPrices = itemList.filter((orderItem: PreorderArticle) => orderItem.price?.purchasePrice);
        const revenue = _.sumBy(itemsWithPrices, (itemWithPrices: PreorderArticle) =>
            getTotalPriceByItem(itemWithPrices)
        );
        const orderItemsForEcommEvent = this.getOrderItemsForEcommEventTracking();

        this.segmentService.track(TrackingEvents.PURCHASE, {
            ecommerce: {
                transaction_id: `${Date.now()}`,
                value: revenue,
                currency: _.head(itemsWithPrices)?.price?.currency,
                items: orderItemsForEcommEvent
            }
        });
    }

    private changed(): void {
        setItemAsJson(this.storageKey, this.listOfItems);
        this.items.next(_.values(this.listOfItems));
    }

    private hasValidItemsForOrder(items: OrderSheetItem[]): boolean {
        const sizes = _.flatMap(items, (item) => item.sizes);
        const prepacks = _.reduce(
            items,
            (orderedPrepacks: PrepackForPreorder[], { prepacks: currentPrepacks }) => [
                ...orderedPrepacks,
                ...(currentPrepacks ? currentPrepacks : [])
            ],
            []
        );
        const validPrepacks = _.filter(prepacks, ({ stock }) => stock.selectedAmount > 0);
        const validSizes = _.filter(sizes, ({ stock }) => stock.selectedAmount > 0);

        return !_.isEmpty(validSizes) || !_.isEmpty(validPrepacks);
    }

    private getItemsToOrder(): OrderedItem[] {
        const filterItemsWithValidAmount = (items) => _.filter(items, ({ stock }) => stock.selectedAmount > 0);
        return _.reduce(
            this.listOfItems,
            (sizesAndPrepacks, { sizes, prepacks, selectedDeliveryDate }) => [
                ...sizesAndPrepacks,
                ...this.mapToOrderItems(
                    filterItemsWithValidAmount(sizes),
                    selectedDeliveryDate,
                    OrderedItemType.Product,
                    'gtin'
                ),
                ...this.mapToOrderItems(
                    filterItemsWithValidAmount(prepacks),
                    selectedDeliveryDate,
                    OrderedItemType.Prepack,
                    '_id'
                )
            ],
            []
        );
    }

    private getOrderItemsForEcommEventTracking() {
        const filterItemsWithValidAmount = (sizes) => _.filter(sizes, ({ stock }) => stock.selectedAmount > 0);

        return _.reduce(
            this.listOfItems,
            (items, { sizes, color }) => [
                ...items,
                ..._.map(filterItemsWithValidAmount(sizes), (size) => ({
                    item_id: size.gtin,
                    item_name: size.name,
                    affiliation: 'preorder',
                    currency: size.price?.currency,
                    discount: size.price?.discount,
                    item_brand: size.brandName,
                    item_list_id: 'related_products',
                    item_list_name: 'Related Products',
                    item_variant: color?.name,
                    price: size.price?.discountedPurchasePrice || size.price?.purchasePrice,
                    quantity: size.stock.selectedAmount
                }))
            ],
            []
        );
    }

    private mapToOrderItems(
        itemsToOrder: Array<PreorderArticleSize & PrepackForPreorder>,
        selectedDeliveryDate,
        type: OrderedItemType,
        itemIdentifier: 'gtin' | '_id'
    ): OrderedItem[] {
        return itemsToOrder.map(({ [itemIdentifier]: identifier, stock }) => ({
            identifier,
            type,
            quantity: stock.selectedAmount,
            deliveryDate: selectedDeliveryDate ? selectedDeliveryDate.value : null
        }));
    }

    private trackClearOrderSheet() {
        const itemList = _.values(this.listOfItems);
        const itemsWithPrices = itemList.filter((orderItem: PreorderArticle) => orderItem.price?.purchasePrice);
        const revenue = _.sumBy(itemsWithPrices, (itemWithPrices: PreorderArticle) =>
            getTotalPriceByItem(itemWithPrices)
        );
        const orderItemsForEcommEvent = this.getOrderItemsForEcommEventTracking();

        this.segmentService.track(TrackingEvents.REMOVE_FROM_CART, {
            ecommerce: {
                value: revenue,
                items: orderItemsForEcommEvent
            }
        });
    }
}

interface OrderSheetList {
    [key: string]: OrderSheetItem;
}

export enum OrderError {
    OrderingProcessError = 'PREORDER.ORDER_SHEET.ERROR_REQUESTED_IS_NOT_AVAILABLE',
    Unknown = 'TEXT_UNKNOWN_ERROR'
}

interface OrderErrorMessage extends CartErrors<OrderError> {
    message: OrderError;
    data: {
        error?: unknown;
    };
}
